import { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  Plus, Users, BarChart3, Mail, Download, LogOut, 
  Check, X, Minus, Copy, RefreshCw, ChevronRight,
  FileText, Clock, AlertCircle, Trash2, Pencil
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_poll-engine-1/artifacts/tvzc3ev3_TMC-Coms-Logo.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard({ onLogout }) {
  const [polls, setPolls] = useState([]);
  const [selectedPoll, setSelectedPoll] = useState(null);
  const [pollDetails, setPollDetails] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterResponse, setFilterResponse] = useState("all");
  
  // Dialog states
  const [showCreatePoll, setShowCreatePoll] = useState(false);
  const [showAddRecipients, setShowAddRecipients] = useState(false);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditPoll, setShowEditPoll] = useState(false);
  
  // Form states
  const [newPollTitle, setNewPollTitle] = useState("");
  const [newPollQuestion, setNewPollQuestion] = useState("");
  const [newPollDeadline, setNewPollDeadline] = useState("");
  const [newPollAnonymous, setNewPollAnonymous] = useState(true);  // Default to anonymous
  const [recipientInput, setRecipientInput] = useState("");
  const [selectedEmailIndex, setSelectedEmailIndex] = useState(0);
  const [anonymousEmail, setAnonymousEmail] = useState(null);
  
  // Edit poll states
  const [editTitle, setEditTitle] = useState("");
  const [editQuestion, setEditQuestion] = useState("");

  const token = localStorage.getItem("admin_token");
  const headers = { 
    "Authorization": `Bearer ${token}`,
    "Content-Type": "application/json"
  };

  // Fetch polls
  const fetchPolls = useCallback(async () => {
    try {
      const res = await fetch(`${API}/polls`, { headers });
      if (res.ok) {
        const data = await res.json();
        setPolls(data);
        if (data.length > 0 && !selectedPoll) {
          setSelectedPoll(data[0].id);
        }
      }
    } catch (error) {
      toast.error("Failed to fetch polls");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch poll details
  const fetchPollDetails = useCallback(async (pollId) => {
    try {
      const res = await fetch(`${API}/polls/${pollId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setPollDetails(data);
      }
    } catch (error) {
      toast.error("Failed to fetch poll details");
    }
  }, []);

  // Fetch recipients
  const fetchRecipients = useCallback(async (pollId) => {
    try {
      const res = await fetch(`${API}/polls/${pollId}/recipients`, { headers });
      if (res.ok) {
        const data = await res.json();
        setRecipients(data);
      }
    } catch (error) {
      toast.error("Failed to fetch recipients");
    }
  }, []);

  useEffect(() => {
    fetchPolls();
  }, [fetchPolls]);

  useEffect(() => {
    if (selectedPoll) {
      fetchPollDetails(selectedPoll);
      fetchRecipients(selectedPoll);
    }
  }, [selectedPoll, fetchPollDetails, fetchRecipients]);

  // Create poll
  const handleCreatePoll = async () => {
    if (!newPollTitle || !newPollQuestion) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const body = {
        title: newPollTitle,
        question: newPollQuestion,
        deadline: newPollDeadline ? new Date(newPollDeadline).toISOString() : null,
        anonymous: newPollAnonymous
      };

      const res = await fetch(`${API}/polls`, {
        method: "POST",
        headers,
        body: JSON.stringify(body)
      });

      if (res.ok) {
        const poll = await res.json();
        toast.success("Poll created successfully");
        setShowCreatePoll(false);
        setNewPollTitle("");
        setNewPollQuestion("");
        setNewPollDeadline("");
        setNewPollAnonymous(true);
        fetchPolls();
        setSelectedPoll(poll.id);
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to create poll");
      }
    } catch (error) {
      toast.error("Failed to create poll");
    }
  };

  // Delete poll
  const handleDeletePoll = async () => {
    try {
      const res = await fetch(`${API}/polls/${selectedPoll}`, {
        method: "DELETE",
        headers
      });

      if (res.ok) {
        toast.success("Poll deleted successfully");
        setShowDeleteConfirm(false);
        setSelectedPoll(null);
        setPollDetails(null);
        fetchPolls();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to delete poll");
      }
    } catch (error) {
      toast.error("Failed to delete poll");
    }
  };

  // Edit poll
  const openEditPoll = () => {
    setEditTitle(pollDetails.title);
    setEditQuestion(pollDetails.question);
    setShowEditPoll(true);
  };

  const handleEditPoll = async () => {
    if (!editTitle || !editQuestion) {
      toast.error("Title and question are required");
      return;
    }

    try {
      const res = await fetch(`${API}/polls/${selectedPoll}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          title: editTitle,
          question: editQuestion
        })
      });

      if (res.ok) {
        toast.success("Poll updated successfully");
        setShowEditPoll(false);
        fetchPollDetails(selectedPoll);
        fetchPolls();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to update poll");
      }
    } catch (error) {
      toast.error("Failed to update poll");
    }
  };

  // Add recipients
  const handleAddRecipients = async () => {
    if (!recipientInput.trim()) {
      toast.error("Please enter recipient data");
      return;
    }

    // Parse CSV-like input: name,email per line
    const lines = recipientInput.trim().split("\n");
    const parsedRecipients = [];

    for (const line of lines) {
      const parts = line.split(",").map(p => p.trim());
      if (parts.length >= 2) {
        parsedRecipients.push({ name: parts[0], email: parts[1] });
      }
    }

    if (parsedRecipients.length === 0) {
      toast.error("No valid recipients found. Use format: Name, Email");
      return;
    }

    try {
      const res = await fetch(`${API}/recipients/bulk`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          poll_id: selectedPoll,
          recipients: parsedRecipients
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Added ${data.count} recipients`);
        setShowAddRecipients(false);
        setRecipientInput("");
        fetchRecipients(selectedPoll);
        fetchPollDetails(selectedPoll);
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to add recipients");
      }
    } catch (error) {
      toast.error("Failed to add recipients");
    }
  };

  // Generate email templates
  const handleGenerateEmails = async () => {
    const baseUrl = window.location.origin;
    
    // Check if poll is anonymous
    if (pollDetails?.anonymous) {
      try {
        const res = await fetch(
          `${API}/polls/${selectedPoll}/anonymous-email?base_url=${encodeURIComponent(baseUrl)}`,
          { headers }
        );

        if (res.ok) {
          const data = await res.json();
          setAnonymousEmail(data);
          setShowEmailPreview(true);
        } else {
          const error = await res.json();
          toast.error(error.detail || "Failed to generate email");
        }
      } catch (error) {
        toast.error("Failed to generate email");
      }
    } else {
      try {
        const res = await fetch(
          `${API}/polls/${selectedPoll}/email-templates?base_url=${encodeURIComponent(baseUrl)}`,
          { headers }
        );

        if (res.ok) {
          const data = await res.json();
          setEmailTemplates(data);
          setSelectedEmailIndex(0);
          setShowEmailPreview(true);
        } else {
          const error = await res.json();
          toast.error(error.detail || "Failed to generate emails");
        }
      } catch (error) {
        toast.error("Failed to generate emails");
      }
    }
  };

  // Export CSV
  const handleExportCSV = async () => {
    try {
      const res = await fetch(`${API}/polls/${selectedPoll}/export`, { headers });
      if (res.ok) {
        const data = await res.json();
        
        // Convert to CSV
        const csvHeaders = ["Name", "Email", "Response", "Responded At"];
        const csvRows = data.data.map(r => [
          r.name,
          r.email,
          r.response,
          r.responded_at || ""
        ]);

        const csvContent = [
          csvHeaders.join(","),
          ...csvRows.map(row => row.map(cell => `"${cell}"`).join(","))
        ].join("\n");

        // Download
        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `poll-results-${selectedPoll}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        
        toast.success("CSV exported successfully");
      }
    } catch (error) {
      toast.error("Failed to export CSV");
    }
  };

  // Copy email HTML
  const copyEmailHtml = (index) => {
    const template = emailTemplates[index];
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(template.html_body)
        .then(() => toast.success(`Email copied for ${template.recipient_name}`))
        .catch(() => {
          // Fallback for clipboard permission issues
          const textarea = document.createElement('textarea');
          textarea.value = template.html_body;
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
          toast.success(`Email copied for ${template.recipient_name}`);
        });
    } else {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = template.html_body;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success(`Email copied for ${template.recipient_name}`);
    }
  };

  // Filter recipients
  const filteredRecipients = recipients.filter(r => {
    if (filterResponse === "all") return true;
    if (filterResponse === "yes") return r.response === "yes";
    if (filterResponse === "no") return r.response === "no";
    if (filterResponse === "neutral") return r.response === "neutral";
    if (filterResponse === "none") return !r.response;
    return true;
  });

  // Get response badge
  const getResponseBadge = (response) => {
    if (!response) {
      return <Badge variant="outline" className="badge-none">No Response</Badge>;
    }
    if (response === "yes") {
      return <Badge className="badge-yes"><Check className="h-3 w-3 mr-1" /> Yes</Badge>;
    }
    if (response === "no") {
      return <Badge className="badge-no"><X className="h-3 w-3 mr-1" /> No</Badge>;
    }
    return <Badge className="badge-neutral"><Minus className="h-3 w-3 mr-1" /> Don't Care</Badge>;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-header sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="TMC" className="h-10" data-testid="dashboard-logo" />
            <div className="hidden sm:block">
              <h1 className="text-lg font-semibold text-foreground">Poll Engine</h1>
              <p className="text-xs text-muted-foreground">Internal Polling System</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            onClick={onLogout}
            className="text-muted-foreground hover:text-foreground"
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} />
            Sign Out
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Poll Selector and Actions */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="flex-1">
            <Select value={selectedPoll || ""} onValueChange={setSelectedPoll}>
              <SelectTrigger className="h-12 bg-card border-border" data-testid="poll-selector">
                <SelectValue placeholder="Select a poll" />
              </SelectTrigger>
              <SelectContent>
                {polls.map(poll => (
                  <SelectItem key={poll.id} value={poll.id}>
                    {poll.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Dialog open={showCreatePoll} onOpenChange={setShowCreatePoll}>
            <DialogTrigger asChild>
              <Button className="h-12 btn-glow active-scale" data-testid="create-poll-btn">
                <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
                New Poll
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle>Create New Poll</DialogTitle>
                <DialogDescription>
                  Set up a new poll for your recipients
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Poll Title (Email Subject)</Label>
                  <Input
                    placeholder="e.g., Quick poll: Demo before rollout?"
                    value={newPollTitle}
                    onChange={(e) => setNewPollTitle(e.target.value)}
                    className="h-12 bg-secondary/50 border-transparent"
                    data-testid="poll-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Question</Label>
                  <Textarea
                    placeholder="e.g., Would you like a short demo of the new support site before it goes live?"
                    value={newPollQuestion}
                    onChange={(e) => setNewPollQuestion(e.target.value)}
                    className="bg-secondary/50 border-transparent min-h-[100px]"
                    data-testid="poll-question-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Deadline (Optional)</Label>
                  <Input
                    type="datetime-local"
                    value={newPollDeadline}
                    onChange={(e) => setNewPollDeadline(e.target.value)}
                    className="h-12 bg-secondary/50 border-transparent"
                    data-testid="poll-deadline-input"
                  />
                </div>
                <div className="flex items-center space-x-3 pt-2">
                  <input
                    type="checkbox"
                    id="anonymous-poll"
                    checked={newPollAnonymous}
                    onChange={(e) => setNewPollAnonymous(e.target.checked)}
                    className="w-5 h-5 rounded bg-secondary border-border text-primary focus:ring-primary"
                    data-testid="poll-anonymous-checkbox"
                  />
                  <Label htmlFor="anonymous-poll" className="cursor-pointer">
                    <span className="font-medium">Anonymous Poll</span>
                    <span className="block text-sm text-muted-foreground">
                      One email for all recipients (BCC-friendly). No individual tracking.
                    </span>
                  </Label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreatePoll(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreatePoll} className="btn-glow" data-testid="confirm-create-poll-btn">
                  Create Poll
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {selectedPoll && pollDetails ? (
          <>
            {/* Poll Info Card */}
            <Card className="bg-card border-border mb-8 tracing-border animate-fade-in">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-2xl">{pollDetails.title}</CardTitle>
                    <CardDescription className="mt-2 text-base">
                      {pollDetails.question}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2 items-center">
                    {pollDetails.anonymous && (
                      <Badge variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/30">
                        Anonymous
                      </Badge>
                    )}
                    <Badge variant={pollDetails.status === "active" ? "default" : "secondary"}>
                      {pollDetails.status}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={openEditPoll}
                      className="text-muted-foreground hover:text-primary hover:bg-primary/10"
                      data-testid="edit-poll-btn"
                    >
                      <Pencil className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="text-muted-foreground hover:text-red-500 hover:bg-red-500/10"
                      data-testid="delete-poll-btn"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
                {pollDetails.deadline && (
                  <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" strokeWidth={1.5} />
                    <span>Deadline: {new Date(pollDetails.deadline).toLocaleString()}</span>
                  </div>
                )}
              </CardHeader>
            </Card>

            {/* Delete Confirmation Dialog */}
            <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
              <DialogContent className="bg-card border-border">
                <DialogHeader>
                  <DialogTitle className="text-red-500">Delete Poll</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to delete "{pollDetails.title}"? This will permanently remove the poll and all responses. This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={handleDeletePoll}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="confirm-delete-poll-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" strokeWidth={1.5} />
                    Delete Poll
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Edit Poll Dialog */}
            <Dialog open={showEditPoll} onOpenChange={setShowEditPoll}>
              <DialogContent className="bg-card border-border">
                <DialogHeader>
                  <DialogTitle>Edit Poll</DialogTitle>
                  <DialogDescription>
                    Update the poll title and question
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Poll Title (Email Subject)</Label>
                    <Input
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="h-12 bg-secondary/50 border-transparent"
                      data-testid="edit-poll-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Question</Label>
                    <Textarea
                      value={editQuestion}
                      onChange={(e) => setEditQuestion(e.target.value)}
                      className="bg-secondary/50 border-transparent min-h-[100px]"
                      data-testid="edit-poll-question-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowEditPoll(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleEditPoll} className="btn-glow" data-testid="confirm-edit-poll-btn">
                    Save Changes
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Stats Grid */}
            <div className={`grid ${pollDetails.anonymous ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2 md:grid-cols-4'} gap-4 mb-8`}>
              {!pollDetails.anonymous && (
                <Card className="bg-card border-border stat-card animate-fade-in">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <Users className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                      <span className="text-xs text-muted-foreground">Recipients</span>
                    </div>
                    <p className="text-3xl font-bold mt-2" data-testid="total-recipients">
                      {pollDetails.total_recipients}
                    </p>
                  </CardContent>
                </Card>
              )}

              <Card className="bg-card border-border stat-card animate-fade-in">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <BarChart3 className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                    <span className="text-xs text-muted-foreground">Total Votes</span>
                  </div>
                  <p className="text-3xl font-bold mt-2" data-testid="total-responses">
                    {pollDetails.total_responses}
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border stat-card animate-fade-in-delay-1">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <Check className="h-5 w-5 text-green-500" strokeWidth={1.5} />
                    <span className="text-xs text-muted-foreground">Yes</span>
                  </div>
                  <p className="text-3xl font-bold mt-2 text-green-500" data-testid="yes-count">
                    {pollDetails.yes_count}
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border stat-card animate-fade-in-delay-1">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <X className="h-5 w-5 text-red-500" strokeWidth={1.5} />
                    <span className="text-xs text-muted-foreground">No</span>
                  </div>
                  <p className="text-3xl font-bold mt-2 text-red-500" data-testid="no-count">
                    {pollDetails.no_count}
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border stat-card animate-fade-in-delay-2">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <Minus className="h-5 w-5 text-slate-400" strokeWidth={1.5} />
                    <span className="text-xs text-muted-foreground">Don't Care</span>
                  </div>
                  <p className="text-3xl font-bold mt-2 text-slate-400" data-testid="neutral-count">
                    {pollDetails.neutral_count}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Response Rate Bar - Only for non-anonymous polls */}
            {!pollDetails.anonymous && (
              <Card className="bg-card border-border mb-8">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium">Response Rate</span>
                    <span className="text-2xl font-bold" data-testid="response-rate">
                      {pollDetails.response_rate}%
                    </span>
                  </div>
                  <div className="h-3 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full flex">
                      <div 
                        className="bg-green-500 transition-all duration-500"
                        style={{ width: `${(pollDetails.yes_count / Math.max(pollDetails.total_recipients, 1)) * 100}%` }}
                      />
                      <div 
                        className="bg-red-500 transition-all duration-500"
                        style={{ width: `${(pollDetails.no_count / Math.max(pollDetails.total_recipients, 1)) * 100}%` }}
                      />
                      <div 
                        className="bg-slate-400 transition-all duration-500"
                        style={{ width: `${(pollDetails.neutral_count / Math.max(pollDetails.total_recipients, 1)) * 100}%` }}
                      />
                    </div>
                  </div>
                  <div className="flex gap-6 mt-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-green-500" /> Yes: {pollDetails.yes_count}
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-red-500" /> No: {pollDetails.no_count}
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-slate-400" /> Neutral: {pollDetails.neutral_count}
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-secondary" /> No Response: {pollDetails.no_response_count}
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Vote Distribution Bar - For anonymous polls */}
            {pollDetails.anonymous && pollDetails.total_responses > 0 && (
              <Card className="bg-card border-border mb-8">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium">Vote Distribution</span>
                    <span className="text-2xl font-bold" data-testid="total-votes">
                      {pollDetails.total_responses} votes
                    </span>
                  </div>
                  <div className="h-3 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full flex">
                      <div 
                        className="bg-green-500 transition-all duration-500"
                        style={{ width: `${(pollDetails.yes_count / pollDetails.total_responses) * 100}%` }}
                      />
                      <div 
                        className="bg-red-500 transition-all duration-500"
                        style={{ width: `${(pollDetails.no_count / pollDetails.total_responses) * 100}%` }}
                      />
                      <div 
                        className="bg-slate-400 transition-all duration-500"
                        style={{ width: `${(pollDetails.neutral_count / pollDetails.total_responses) * 100}%` }}
                      />
                    </div>
                  </div>
                  <div className="flex gap-6 mt-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-green-500" /> Yes: {pollDetails.yes_count} ({pollDetails.total_responses > 0 ? Math.round(pollDetails.yes_count / pollDetails.total_responses * 100) : 0}%)
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-red-500" /> No: {pollDetails.no_count} ({pollDetails.total_responses > 0 ? Math.round(pollDetails.no_count / pollDetails.total_responses * 100) : 0}%)
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-slate-400" /> Neutral: {pollDetails.neutral_count} ({pollDetails.total_responses > 0 ? Math.round(pollDetails.neutral_count / pollDetails.total_responses * 100) : 0}%)
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tabs Section */}
            <Tabs defaultValue={pollDetails.anonymous ? "emails" : "recipients"} className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <TabsList className="bg-secondary">
                  {!pollDetails.anonymous && (
                    <TabsTrigger value="recipients" data-testid="recipients-tab">
                      <Users className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      Recipients
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="emails" data-testid="emails-tab">
                    <Mail className="h-4 w-4 mr-2" strokeWidth={1.5} />
                    {pollDetails.anonymous ? "Email Template" : "Emails"}
                  </TabsTrigger>
                </TabsList>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      fetchPollDetails(selectedPoll);
                      if (!pollDetails.anonymous) fetchRecipients(selectedPoll);
                    }}
                    data-testid="refresh-btn"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" strokeWidth={1.5} />
                    Refresh
                  </Button>
                  {!pollDetails.anonymous && (
                    <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
                      <Download className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      Export CSV
                    </Button>
                  )}
                </div>
              </div>

              {!pollDetails.anonymous && (
                <TabsContent value="recipients">
                <Card className="bg-card border-border">
                  <CardHeader className="pb-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <CardTitle className="text-lg">Recipients</CardTitle>
                      <div className="flex gap-2">
                        <Select value={filterResponse} onValueChange={setFilterResponse}>
                          <SelectTrigger className="w-[150px] bg-secondary border-transparent" data-testid="filter-select">
                            <SelectValue placeholder="Filter" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="yes">Yes</SelectItem>
                            <SelectItem value="no">No</SelectItem>
                            <SelectItem value="neutral">Don't Care</SelectItem>
                            <SelectItem value="none">No Response</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        <Dialog open={showAddRecipients} onOpenChange={setShowAddRecipients}>
                          <DialogTrigger asChild>
                            <Button className="btn-glow active-scale" data-testid="add-recipients-btn">
                              <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
                              Add Recipients
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-card border-border max-w-lg">
                            <DialogHeader>
                              <DialogTitle>Add Recipients</DialogTitle>
                              <DialogDescription>
                                Enter recipients in CSV format: Name, Email (one per line)
                              </DialogDescription>
                            </DialogHeader>
                            <div className="py-4">
                              <Textarea
                                placeholder={"John Smith, john@example.com\nJane Doe, jane@example.com"}
                                value={recipientInput}
                                onChange={(e) => setRecipientInput(e.target.value)}
                                className="bg-secondary/50 border-transparent min-h-[200px] font-mono text-sm"
                                data-testid="recipients-textarea"
                              />
                            </div>
                            <DialogFooter>
                              <Button variant="outline" onClick={() => setShowAddRecipients(false)}>
                                Cancel
                              </Button>
                              <Button onClick={handleAddRecipients} className="btn-glow" data-testid="confirm-add-recipients-btn">
                                Add Recipients
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    {filteredRecipients.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <Users className="h-12 w-12 mx-auto mb-4 opacity-50" strokeWidth={1} />
                        <p>No recipients found</p>
                        <p className="text-sm mt-1">Add recipients to get started</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="border-border hover:bg-transparent">
                              <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Name</TableHead>
                              <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Email</TableHead>
                              <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Response</TableHead>
                              <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Responded At</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredRecipients.map((recipient, idx) => (
                              <TableRow 
                                key={recipient.id} 
                                className="border-border table-row-hover"
                                data-testid={`recipient-row-${idx}`}
                              >
                                <TableCell className="font-medium">{recipient.name}</TableCell>
                                <TableCell className="text-muted-foreground">{recipient.email}</TableCell>
                                <TableCell>{getResponseBadge(recipient.response)}</TableCell>
                                <TableCell className="text-muted-foreground text-sm">
                                  {recipient.responded_at 
                                    ? new Date(recipient.responded_at).toLocaleString() 
                                    : "-"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              )}

              <TabsContent value="emails">
                <Card className="bg-card border-border">
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <CardTitle className="text-lg">
                          {pollDetails.anonymous ? "Email Template (BCC-Friendly)" : "Email Templates"}
                        </CardTitle>
                        <CardDescription>
                          {pollDetails.anonymous 
                            ? "Generate one email to send to all recipients via BCC"
                            : "Generate personalized emails for each recipient"
                          }
                        </CardDescription>
                      </div>
                      <Button 
                        onClick={handleGenerateEmails} 
                        className="btn-glow active-scale"
                        data-testid="generate-emails-btn"
                      >
                        <Mail className="h-4 w-4 mr-2" strokeWidth={1.5} />
                        Generate Email{pollDetails.anonymous ? "" : "s"}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" strokeWidth={1} />
                      <p>Click "Generate Email{pollDetails.anonymous ? "" : "s"}" to create {pollDetails.anonymous ? "your" : "personalized"} template{pollDetails.anonymous ? "" : "s"}</p>
                      {pollDetails.anonymous ? (
                        <p className="text-sm mt-1">
                          One email with shared voting links - perfect for BCC
                        </p>
                      ) : (
                        <p className="text-sm mt-1">
                          Each recipient will get unique voting links
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Email Preview Dialog */}
            <Dialog open={showEmailPreview} onOpenChange={setShowEmailPreview}>
              <DialogContent className="bg-card border-border max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader>
                  <DialogTitle>
                    {pollDetails?.anonymous ? "Email Template (BCC-Friendly)" : "Email Templates"}
                  </DialogTitle>
                  <DialogDescription>
                    {pollDetails?.anonymous 
                      ? "Copy this HTML and send to all recipients via BCC"
                      : "Copy HTML for each recipient to send via your email system"
                    }
                  </DialogDescription>
                </DialogHeader>
                
                {/* Anonymous Poll - Single Email */}
                {pollDetails?.anonymous && anonymousEmail && (
                  <div className="flex-1 overflow-y-auto mt-4">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">To:</p>
                          <p className="font-medium">All Recipients (use BCC)</p>
                        </div>
                      </div>

                      <div>
                        <p className="text-sm text-muted-foreground">Subject (copy this):</p>
                        <div className="flex items-center gap-2">
                          <p className="font-medium flex-1">{anonymousEmail.subject}</p>
                          <Button 
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(anonymousEmail.subject);
                              toast.success("Subject copied!");
                            }}
                          >
                            <Copy className="h-3 w-3 mr-1" /> Copy
                          </Button>
                        </div>
                      </div>

                      <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                        <p className="text-sm text-yellow-200 font-medium mb-2">How to use in Outlook:</p>
                        <ol className="text-sm text-yellow-200/80 list-decimal list-inside space-y-1">
                          <li>Click inside the preview below</li>
                          <li>Press Ctrl+A to select all</li>
                          <li>Press Ctrl+C to copy</li>
                          <li>In Outlook, create new email and paste with Ctrl+V</li>
                          <li>Add recipients in BCC field</li>
                        </ol>
                      </div>

                      <div className="border border-border rounded-lg overflow-hidden">
                        <div className="bg-secondary/50 px-4 py-2 text-xs text-muted-foreground uppercase tracking-wider flex justify-between items-center">
                          <span>Click below, then Ctrl+A, Ctrl+C to copy</span>
                        </div>
                        <iframe
                          srcDoc={anonymousEmail.html_body}
                          className="w-full bg-white"
                          style={{ height: '400px', border: 'none' }}
                          title="Email Preview"
                        />
                      </div>

                      <div className="text-sm text-muted-foreground">
                        <p className="font-medium mb-2">Or copy individual voting links:</p>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="text-green-500 w-16">Yes:</span>
                            <code className="flex-1 text-xs bg-secondary/50 p-2 rounded break-all">{anonymousEmail.yes_url}</code>
                            <Button variant="outline" size="sm" onClick={() => {
                              navigator.clipboard.writeText(anonymousEmail.yes_url);
                              toast.success("Yes link copied!");
                            }}><Copy className="h-3 w-3" /></Button>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-red-500 w-16">No:</span>
                            <code className="flex-1 text-xs bg-secondary/50 p-2 rounded break-all">{anonymousEmail.no_url}</code>
                            <Button variant="outline" size="sm" onClick={() => {
                              navigator.clipboard.writeText(anonymousEmail.no_url);
                              toast.success("No link copied!");
                            }}><Copy className="h-3 w-3" /></Button>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-slate-400 w-16">Neutral:</span>
                            <code className="flex-1 text-xs bg-secondary/50 p-2 rounded break-all">{anonymousEmail.neutral_url}</code>
                            <Button variant="outline" size="sm" onClick={() => {
                              navigator.clipboard.writeText(anonymousEmail.neutral_url);
                              toast.success("Neutral link copied!");
                            }}><Copy className="h-3 w-3" /></Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Non-Anonymous Poll - Multiple Emails */}
                {!pollDetails?.anonymous && emailTemplates.length > 0 && (
                  <div className="flex-1 overflow-hidden flex flex-col md:flex-row gap-4 mt-4">
                    {/* Recipient List */}
                    <div className="w-full md:w-64 shrink-0 border-r border-border pr-4 overflow-y-auto max-h-[300px] md:max-h-none">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                        Recipients ({emailTemplates.length})
                      </p>
                      {emailTemplates.map((template, idx) => (
                        <button
                          key={idx}
                          onClick={() => setSelectedEmailIndex(idx)}
                          className={`w-full text-left p-3 rounded-lg mb-1 transition-colors ${
                            selectedEmailIndex === idx 
                              ? "bg-primary/10 border border-primary/30" 
                              : "hover:bg-secondary"
                          }`}
                          data-testid={`email-recipient-${idx}`}
                        >
                          <p className="font-medium text-sm truncate">{template.recipient_name}</p>
                          <p className="text-xs text-muted-foreground truncate">{template.recipient_email}</p>
                        </button>
                      ))}
                    </div>

                    {/* Email Preview */}
                    <div className="flex-1 overflow-y-auto">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">To:</p>
                            <p className="font-medium">{emailTemplates[selectedEmailIndex].recipient_email}</p>
                          </div>
                          <Button 
                            onClick={() => copyEmailHtml(selectedEmailIndex)}
                            className="btn-glow active-scale"
                            data-testid="copy-email-btn"
                          >
                            <Copy className="h-4 w-4 mr-2" strokeWidth={1.5} />
                            Copy HTML
                          </Button>
                        </div>

                        <div>
                          <p className="text-sm text-muted-foreground">Subject:</p>
                          <p className="font-medium">{emailTemplates[selectedEmailIndex].subject}</p>
                        </div>

                        <div className="border border-border rounded-lg overflow-hidden">
                          <div className="bg-secondary/50 px-4 py-2 text-xs text-muted-foreground uppercase tracking-wider">
                            Preview
                          </div>
                          <div 
                            className="p-4 bg-white"
                            dangerouslySetInnerHTML={{ __html: emailTemplates[selectedEmailIndex].html_body }}
                          />
                        </div>

                        <div className="text-sm text-muted-foreground">
                          <p className="font-medium mb-2">Direct Links:</p>
                          <div className="space-y-1 font-mono text-xs bg-secondary/50 p-3 rounded-lg break-all">
                            <p><span className="text-green-500">Yes:</span> {emailTemplates[selectedEmailIndex].yes_url}</p>
                            <p><span className="text-red-500">No:</span> {emailTemplates[selectedEmailIndex].no_url}</p>
                            <p><span className="text-slate-400">Neutral:</span> {emailTemplates[selectedEmailIndex].neutral_url}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </>
        ) : (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center">
              <BarChart3 className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" strokeWidth={1} />
              <h2 className="text-xl font-semibold mb-2">No Polls Yet</h2>
              <p className="text-muted-foreground mb-6">Create your first poll to get started</p>
              <Button onClick={() => setShowCreatePoll(true)} className="btn-glow active-scale">
                <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Create Your First Poll
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
